import { getAccessTokenSilentlyWithMonitoring } from '@/utils/auth0';
import { oneSignalExternalId } from '@/utils/onesignal-external-id';

// giving-api trusts only the `https://heartofgodchurch.org/member_id` claim in the Auth0 access
// token. When a leader links a login that had no member (or an Acts undo moves it back), hogc-one-api
// follows the new link server-side but the token on the device still carries the old claim, so
// every MyGiving call 403s until the member signs out and in (prod 2026-11-05).
//
// The profile already tells us who the login is now: `oneSignalExternalId` is the member id, or
// `au-{appUserId}` for a login with no member. When that changes against the last profile seen
// for the same Auth0 user, fetch a fresh access token once, bypassing the SDK cache.
//
// The last identity is persisted so a cold start after the link refreshes too. It is recorded
// before the refresh is attempted, so a failed refresh is tried once per change, never in a loop.

export const LAST_TOKEN_IDENTITY_KEY = 'hogc_last_token_identity';
const REFRESH_SOURCE = 'member-link-changed';

export function readLastTokenIdentity() {
  try {
    const raw = localStorage.getItem(LAST_TOKEN_IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.identity === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function writeLastTokenIdentity(identity, authSub) {
  try {
    localStorage.setItem(
      LAST_TOKEN_IDENTITY_KEY,
      JSON.stringify({ identity, authSub: authSub ?? null }),
    );
  } catch (error) {
    console.warn('[MemberLinkToken] Could not persist the last identity:', error?.message);
  }
}

export function clearLastTokenIdentity() {
  try {
    localStorage.removeItem(LAST_TOKEN_IDENTITY_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Whether the login's member link moved between two profile loads. A first load (no previous
 * identity), a profile that names no identity, or a different Auth0 user (another sign-in on the
 * device, whose token is already fresh) is not a change.
 *
 * @param {{ identity: string, authSub: string|null }|null} previous
 * @param {string|null} identity this profile's external identity
 * @param {string|null} authSub this session's Auth0 `sub`
 * @returns {boolean}
 */
export function memberLinkChanged(previous, identity, authSub) {
  if (!previous?.identity || !identity) return false;
  if (previous.authSub && authSub && previous.authSub !== authSub) return false;
  return previous.identity !== identity;
}

/**
 * Compare this profile with the last one seen and, when the member link moved, force a fresh
 * Auth0 access token and hand it to `onToken`. Never rejects: a failed refresh is logged and the
 * session carries on with the token it has.
 *
 * @param {{ profile: object, authSub?: string|null, onToken: (token: string) => void }} options
 * @returns {Promise<boolean>} whether a fresh token was stored
 */
export async function refreshTokenIfMemberLinkChanged({ profile, authSub = null, onToken }) {
  const identity = oneSignalExternalId(profile);
  if (!identity) return false;

  const previous = readLastTokenIdentity();
  const changed = memberLinkChanged(previous, identity, authSub);
  if (previous?.identity !== identity || previous?.authSub !== (authSub ?? null)) {
    writeLastTokenIdentity(identity, authSub);
  }
  if (!changed) return false;

  console.info('[MemberLinkToken] Member link changed; refreshing the access token');
  try {
    const token = await getAccessTokenSilentlyWithMonitoring({ cacheMode: 'off' }, REFRESH_SOURCE);
    if (!token) throw new Error('Silent refresh returned no token');
    onToken(token);
    return true;
  } catch (error) {
    console.warn('[MemberLinkToken] Token refresh failed; keeping the current token', {
      error: error?.error || error?.message,
    });
    return false;
  }
}
